import * as React from 'react';
import { Badge, SpaceBetween, Link, Box } from '@amzn/awsui-components-console';
import { TaskTypeListItem } from 'src/stores/taskTypeListStore/taskTypeListStore.interface';

export const formatExecutionTime = (time: {
	days: number;
	hours: number;
	minutes: number;
}) => {
	if (!time) return '-';

	const parts: string[] = [];

	if (time.days) parts.push(`${time.days}d`);

	if (time.hours) parts.push(`${time.hours}h`);

	if (time.minutes) parts.push(`${time.minutes}m`);

	return parts.length ? parts.join(' ') : '< 1m';
};

export const COLUMN_DEFINITIONS = [
	{
		id: 'taskName',
		header: 'Task Name',
		cell: (item: TaskTypeListItem) => item.taskName,
		sortingField: 'taskName',
		width: 250,
	},
	{
		id: 'applicationName',
		header: 'Application',
		cell: (item: TaskTypeListItem) => item.applicationName || 'SNOWPlatform',
		sortingField: 'applicationName',
	},
	{
		id: 'description',
		header: 'Description',
		cell: (item: TaskTypeListItem) => item.description || '-',
		width: 300,
	},
	{
		id: 'type',
		header: 'Type',
		cell: (item: TaskTypeListItem) => (
			<Badge color={item.operation ? 'green' : 'blue'}>
				{item.operation ? 'Operation' : 'Task'}
			</Badge>
		),
	},
	{
		id: 'owner',
		header: 'Owner',
		cell: (item: TaskTypeListItem) => item.owner || '-',
	},
	{
		id: 'entityTypeSupported',
		header: 'Entity Type',
		cell: (item: TaskTypeListItem) => item.entityTypeSupported || '-',
	},
	{
		id: 'averageExecutionTime',
		header: 'Avg. Time',
		cell: (item: TaskTypeListItem) =>
			item.averageExecutionTime
				? formatExecutionTime(item.averageExecutionTime)
				: '-',
	},
	{
		id: 'taskInputs',
		header: 'Inputs',
		cell: (item: TaskTypeListItem) =>
			item.taskInputs?.length
				? item.taskInputs
						.filter((input) => input?.attributeName)
						.map((input) => input.attributeName)
						.join(', ') || '-'
				: '-',
		width: 200,
	},
	{
		id: 'taskOutputs',
		header: 'Outputs',
		cell: (item: TaskTypeListItem) =>
			item.taskOutputs?.length
				? item.taskOutputs
						.filter((output) => output?.attributeName)
						.map((output) => output.attributeName)
						.join(', ') || '-'
				: '-',
		width: 200,
	},
	{
		id: 'version',
		header: 'Version',
		cell: (item: TaskTypeListItem) => item.version || 1,
	},
	{
		id: 'taskAccessBindles',
		header: 'Access Bindles',
		cell: (item: TaskTypeListItem) =>
			item.taskAccessBindles?.length ? (
				<SpaceBetween size="xs" direction="horizontal">
					{item.taskAccessBindles.map((bindle: string) => (
						<Link
							key={bindle}
							href={`https://bindles.amazon.com/resource/${bindle}`}
							external
						>
							{bindle.replace('amzn1.bindle.resource.', '')}
						</Link>
					))}
				</SpaceBetween>
			) : (
				'-'
			),
		sortingComparator: (a: TaskTypeListItem, b: TaskTypeListItem) =>
			(a.taskAccessBindles?.length || 0) - (b.taskAccessBindles?.length || 0),
		width: 250,
	},
];

export const DEFAULT_COLUMN_DISPLAY = [
	{ id: 'taskName', visible: true },
	{ id: 'applicationName', visible: true },
	{ id: 'description', visible: true },
	{ id: 'type', visible: true },
	{ id: 'owner', visible: false },
	{ id: 'entityTypeSupported', visible: false },
	{ id: 'averageExecutionTime', visible: false },
	{ id: 'taskInputs', visible: false },
	{ id: 'taskOutputs', visible: false },
	{ id: 'version', visible: false },
	{ id: 'taskAccessBindles', visible: false },
];

export const PAGE_SIZE_PREFERENCE = {
	title: 'Page size',
	options: [
		{ value: 10, label: '10 items' },
		{ value: 20, label: '20 items' },
		{ value: 50, label: '50 items' },
	],
};

export const STRIPED_ROWS_PREFERENCE = {
	label: 'Striped rows',
	description: 'Select to add alternating shaded rows',
};

export const WRAP_LINES_PREFERENCE = {
	label: 'Wrap lines',
	description: 'Select to wrap long text in cells',
};

export const HELP_PANEL_CONFIG = {
	title: <h2>About Supported Task Types</h2>,
	content: (
		<SpaceBetween size="m">
			<Box variant="p">
				This page displays all available task types and operations that can be
				used when creating workflows.
			</Box>
			<Box variant="h4">Tasks vs Operations</Box>
			<Box variant="p">
				<strong>Tasks</strong> are pre-built automation units provided by the
				SNOW platform for common operations like creating nodes in FCCS, DJS
				creation, and ETL creation.
			</Box>
			<Box variant="p">
				<strong>Operations</strong> are custom API integrations created by
				application teams to extend workflow capabilities with service-specific
				functionality.
			</Box>
			<Box variant="h4">Using in Workflows</Box>
			<Box variant="p">
				When creating a workflow, you can drag any task or operation from the
				task panel onto the canvas to include it in your workflow definition.
			</Box>
			<Box variant="h4">Column Information</Box>
			<ul style={{ margin: 0, paddingLeft: '20px' }}>
				<li>
					<strong>Inputs</strong> - Required data the task needs to execute
				</li>
				<li>
					<strong>Outputs</strong> - Data produced by the task after execution
				</li>
				<li>
					<strong>Access Bindles</strong> - Permission groups that control who
					can use the task
				</li>
			</ul>
		</SpaceBetween>
	),
	links: [
		{
			href: 'https://w.amazon.com/bin/view/SNOW/Service_Onboarding/Application_Operation_Onboarding',
			text: 'Application & Operation Onboarding',
		},
	],
};
